/**
 * Consigne de l'exercice.
 *
 * Ce bloc n'existait pas : le champ etait saisi en configuration, enregistre et
 * traduit par le serveur, mais aucune vue eleve ne l'affichait. L'exercice de
 * type "reproduire" etait donc impossible, faute de pouvoir montrer le rendu
 * cible.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { prepareStatement } from '../core/statement';

interface HtmlCssStatementProps {
  statement?: string | null;
  /** Identifie la lecon pour memoriser l'etat plie/deplie. */
  lessonId?: number;
  /** Replie par defaut la ou la hauteur est comptee (mobile). */
  defaultCollapsed?: boolean;
}

const collapseKey = (lessonId?: number) =>
  lessonId ? `htmlcss_statement_collapsed_${lessonId}` : null;

/** Relit la preference de pliage, sans echouer si le stockage est indisponible. */
const readCollapsed = (lessonId: number | undefined, fallback: boolean): boolean => {
  const key = collapseKey(lessonId);
  if (!key) return fallback;
  try {
    const stored = window.localStorage.getItem(key);
    return stored === null ? fallback : stored === '1';
  } catch {
    return fallback;
  }
};

const HtmlCssStatement: React.FC<HtmlCssStatementProps> = ({
  statement,
  lessonId,
  defaultCollapsed = false,
}) => {
  const { t } = useTranslation();
  const rendering = prepareStatement(statement);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(lessonId, defaultCollapsed));

  useEffect(() => {
    setCollapsed(readCollapsed(lessonId, defaultCollapsed));
  }, [lessonId, defaultCollapsed]);

  const toggle = useCallback(() => {
    setCollapsed(current => {
      const next = !current;
      const key = collapseKey(lessonId);
      if (key) {
        try {
          window.localStorage.setItem(key, next ? '1' : '0');
        } catch {
          // Preference non memorisee : sans consequence sur l'affichage.
        }
      }
      return next;
    });
  }, [lessonId]);

  // Aucun cadre vide lorsqu'il n'y a pas de consigne.
  if (rendering.kind === 'empty') return null;

  return (
    <section className="htmlcss-statement" aria-label={t('htmlCss.statement.label')}>
      <header className="htmlcss-statement-header">
        <h2 className="htmlcss-statement-title">{t('htmlCss.statement.title')}</h2>
        <Button
          type="text"
          size="small"
          onClick={toggle}
          aria-expanded={!collapsed}
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
        >
          {collapsed ? t('htmlCss.statement.expand') : t('htmlCss.statement.collapse')}
        </Button>
      </header>

      {!collapsed && (
        <div className="htmlcss-statement-body">
          {rendering.kind === 'html' ? (
            // Contenu deja assaini par `prepareStatement`.
            <div dangerouslySetInnerHTML={{ __html: rendering.html }} />
          ) : (
            <p className="htmlcss-statement-text">{rendering.text}</p>
          )}
        </div>
      )}
    </section>
  );
};

export default HtmlCssStatement;
